import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSuggest } from '../../hooks/useSuggest';
import { SuggestItem } from '../../config/mapyApi';
import './SuggestInput.css';

interface SuggestInputProps {
    /** Placeholder text */
    placeholder?: string;
    /** Callback při výběru položky */
    onSelect: (item: {
        name: string;
        label: string;
        latitude: number;
        longitude: number;
        location: string;
    }) => void;
    /** Callback při změně hodnoty inputu */
    onChange?: (value: string) => void;
    /** Omezení na lokalitu (default: 'CZ') */
    locality?: string;
    /** CSS třída pro input */
    className?: string;
    /** Výchozí hodnota */
    defaultValue?: string;
    /** Disabled stav */
    disabled?: boolean;
    /** ID inputu pro label */
    id?: string;
}

/**
 * Komponenta pro vyhledávání míst pomocí Mapy.com Suggest API
 *
 * Nahrazuje staré SMap.Suggest
 *
 * @example
 * <SuggestInput
 *     placeholder="Zadejte místo..."
 *     onSelect={(item) => {
 *         console.log(item.name, item.latitude, item.longitude);
 *     }}
 * />
 */
export const SuggestInput: React.FC<SuggestInputProps> = ({
    placeholder = 'Zadejte místo...',
    onSelect,
    onChange,
    locality = 'CZ',
    className = '',
    defaultValue = '',
    disabled = false,
    id,
}) => {
    const [inputValue, setInputValue] = useState(defaultValue);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { suggestions, loading, search, clear } = useSuggest({ locality });

    // Zavřít dropdown při kliknutí mimo
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setHighlightedIndex(-1);
        onChange?.(value);

        if (value.length >= 2) {
            search(value);
            setShowDropdown(true);
        } else {
            clear();
            setShowDropdown(false);
        }
    }, [search, clear, onChange]);

    const handleSelect = useCallback((item: SuggestItem) => {
        setInputValue(item.name);
        setShowDropdown(false);
        clear();

        onSelect({
            name: item.name,
            label: item.label,
            latitude: item.position.lat,
            longitude: item.position.lon,
            location: item.location,
        });
    }, [onSelect, clear]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown || suggestions.length === 0) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    handleSelect(suggestions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                break;
        }
    }, [showDropdown, suggestions, highlightedIndex, handleSelect]);

    const handleFocus = useCallback(() => {
        if (suggestions.length > 0) {
            setShowDropdown(true);
        }
    }, [suggestions]);

    return (
        <div className="suggest-input-container" ref={containerRef}>
            <input
                ref={inputRef}
                id={id}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                placeholder={placeholder}
                disabled={disabled}
                className={`suggest-input ${className}`}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={showDropdown}
                aria-haspopup="listbox"
            />

            {loading && (
                <div className="suggest-loading">
                    <span className="suggest-loading-spinner" />
                </div>
            )}

            {showDropdown && suggestions.length > 0 && (
                <div className="suggest-dropdown">
                    <ul className="suggest-list" role="listbox">
                        {suggestions.map((item, index) => (
                            <li
                                key={`${item.name}-${item.position.lat}-${item.position.lon}`}
                                className={`suggest-item ${index === highlightedIndex ? 'suggest-item-highlighted' : ''}`}
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                role="option"
                                aria-selected={index === highlightedIndex}
                            >
                                <span className="suggest-item-name">{item.name}</span>
                                <span className="suggest-item-location">{item.label}, {item.location}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="suggest-footer">
                        <span>Powered by </span>
                        <img
                            src="https://api.mapy.com/img/api/logo-small.svg"
                            alt="Mapy.com"
                            className="suggest-footer-logo"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuggestInput;
